import { Box } from '@mui/material';
import styled from 'styled-components';
import {
  CustomBarChart,
  Typography,
  colors,
  IconGraphy,
  Avatar,
  styles,
  Dropdown,
  ChartFooterChard,
} from '@leapeasy/ui-kit';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ReactComponent as DashboardEmptyStateIcon } from 'assets/dashboard-empty-state.svg';
import { appStageTotalsHaveValues, sumAppStageCountsForYear } from 'utils/appStageCountsByYear';
import {
  buildAppDistributionYearList,
  getInProgressStageBarPercent,
  getInProgressStageBaseline,
  getPreferredAppDistributionYear,
  resolveAppDistributionYear,
} from 'utils/appDistributionDisplay';
import { toChartYear } from 'utils/claimChartYears';

/** Matches Applications page stage filters (see applicationPrefilters). */
const DISTRIBUTION_STAGE_FILTERS = {
  ApplicationReceived: '01 - Tenant Evaluation',
  DocumentVerification: '02 - Document Verification and Onboarding',
  FinalReview: '02A - Final Review',
  Payment: '04 - Agreement Signed',
  PolicyIssued: '05 - Policy Issued',
};

const MONTHLY_FOOTER_CARDS = [
  {
    shortLabel: 'Received',
    filterKey: DISTRIBUTION_STAGE_FILTERS.ApplicationReceived,
    totalKey: 'stage01',
  },
  {
    shortLabel: 'Verification',
    filterKey: DISTRIBUTION_STAGE_FILTERS.DocumentVerification,
    totalKey: 'stage02',
  },
  {
    shortLabel: 'Final review',
    filterKey: DISTRIBUTION_STAGE_FILTERS.FinalReview,
    totalKey: 'stage02A',
  },
  {
    shortLabel: 'Payment',
    filterKey: DISTRIBUTION_STAGE_FILTERS.Payment,
    totalKey: 'stage04',
  },
  {
    shortLabel: 'Issued',
    filterKey: DISTRIBUTION_STAGE_FILTERS.PolicyIssued,
    totalKey: 'stage05',
  },
];

const toYear = (value) => {
  const rawValue = value && typeof value === 'object' && 'value' in value ? value.value : value;
  return toChartYear(rawValue);
};

export const AppDistribution = ({ data, onApplicationsDrilldown }) => {
  const currentYear = new Date().getFullYear();
  const snapshotCounts = {
    stage01: Number(data?.stage01 || 0),
    stage02: Number(data?.stage02 || 0),
    stage02A: Number(data?.stage02A || 0),
    stage04: Number(data?.stage04 || 0),
    stage05: Number(data?.stage05 || 0),
  };
  const inProgressBaseline = getInProgressStageBaseline(snapshotCounts);

  const inProgressItems = [
    {
      value: snapshotCounts.stage01,
      title: 'Resident Evaluation',
      color: colors.purpleIrish[100],
      backgroundOpacity: 0.3,
      drilldownKey: DISTRIBUTION_STAGE_FILTERS.ApplicationReceived,
    },
    {
      value: snapshotCounts.stage02,
      title: 'Document Verification',
      color: colors.parisGreen[100],
      backgroundOpacity: 0.3,
      drilldownKey: DISTRIBUTION_STAGE_FILTERS.DocumentVerification,
    },
    {
      value: snapshotCounts.stage02A,
      title: 'Final Review',
      color: colors.magentaRed[100],
      backgroundOpacity: 0.3,
      drilldownKey: DISTRIBUTION_STAGE_FILTERS.FinalReview,
    },
    {
      value: snapshotCounts.stage04,
      title: 'Payment',
      color: colors.warmBlue[100],
      backgroundOpacity: 0.3,
      drilldownKey: DISTRIBUTION_STAGE_FILTERS.Payment,
    },
  ];

  const policyIssuedItem = {
    value: snapshotCounts.stage05,
    title: 'Policy Issued',
    color: colors.tomato[100],
    drilldownKey: DISTRIBUTION_STAGE_FILTERS.PolicyIssued,
  };

  const monthlyAggregates = Array.isArray(data?.appStageCountsByMonth)
    ? data.appStageCountsByMonth
    : [];

  const yearList = useMemo(
    () => buildAppDistributionYearList(monthlyAggregates, currentYear),
    [monthlyAggregates, currentYear],
  );

  const userSelectedYearRef = useRef(false);
  const [year, setYear] = useState(() =>
    getPreferredAppDistributionYear(yearList, monthlyAggregates, currentYear),
  );

  useEffect(() => {
    setYear((previousYear) => {
      if (userSelectedYearRef.current) {
        return resolveAppDistributionYear(yearList, monthlyAggregates, previousYear, currentYear);
      }
      return getPreferredAppDistributionYear(yearList, monthlyAggregates, currentYear);
    });
  }, [yearList, monthlyAggregates, currentYear]);

  const selectedYearStageTotals = useMemo(
    () => sumAppStageCountsForYear(monthlyAggregates, year),
    [monthlyAggregates, year],
  );

  const monthlyHasValues = appStageTotalsHaveValues(selectedYearStageTotals);

  const maxInProgressStageTotal = getInProgressStageBaseline(selectedYearStageTotals);

  const chartMax = useMemo(() => {
    if (maxInProgressStageTotal <= 0) return 1;
    const step = Math.max(1, Math.ceil(maxInProgressStageTotal / 4));
    return step * 4;
  }, [maxInProgressStageTotal]);

  const yTicks = useMemo(
    () => [0, 0.25, 0.5, 0.75, 1].map((ratio) => Math.round(chartMax * ratio)),
    [chartMax],
  );

  const monthlyStageBarData = useMemo(
    () => [
      {
        name: 'Resident Evaluation',
        value: selectedYearStageTotals.stage01,
        color: colors.purpleIrish[100],
      },
      {
        name: 'Document Verification',
        value: selectedYearStageTotals.stage02,
        color: colors.parisGreen[100],
      },
      {
        name: 'Final Review',
        value: selectedYearStageTotals.stage02A,
        color: colors.magentaRed[100],
      },
      {
        name: 'Payment',
        value: selectedYearStageTotals.stage04,
        color: colors.warmBlue[100],
      },
    ],
    [selectedYearStageTotals],
  );

  const formatVolumeTick = (value) => {
    const numericValue = Number(value) || 0;
    if (numericValue === 0) return '0';
    if (numericValue >= 1_000_000) return `${numericValue / 1_000_000}M`;
    if (numericValue >= 1000) {
      return numericValue % 1000 === 0
        ? `${numericValue / 1000}k`
        : `${(numericValue / 1000).toFixed(1)}k`;
    }
    return String(Math.round(numericValue));
  };

  const renderStageRow = (item, { showBar, barPercent } = {}) => (
    <Box
      key={item.title}
      display="flex"
      flexDirection="column"
      gap="6px"
      onClick={() => item.drilldownKey && onApplicationsDrilldown?.(item.drilldownKey)}
      sx={{
        ...(item.drilldownKey && onApplicationsDrilldown
          ? { cursor: 'pointer', '&:hover': { opacity: 0.88 } }
          : {}),
      }}
    >
      <Box display="flex" justifyContent="space-between">
        <Box display="flex" gap="4px" alignItems="center">
          <Box
            sx={{
              width: '6px',
              height: '6px',
              background: item.color,
              borderRadius: '3px',
            }}
          />
          <Typography variant="small" style={{ fontWeight: 500, color: colors.black[900] }}>
            {item.title}
          </Typography>
        </Box>

        <Typography variant="small" style={{ fontWeight: 500, color: colors.black[900] }}>
          {Number(item.value).toLocaleString()}
        </Typography>
      </Box>

      {showBar ? (
        <Box sx={{ height: '8px', width: '100%', position: 'relative' }}>
          <Box
            sx={{
              width: '100%',
              height: '100%',
              background: item.color,
              opacity: item.backgroundOpacity,
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              left: '0px',
              top: '0px',
              height: '100%',
              width: `${barPercent}%`,
              background: item.color,
            }}
          />
        </Box>
      ) : null}
    </Box>
  );

  return (
    <AppDistributionContainer
      boxShadow={styles.boxShadow.sm}
      borderRadius={`${styles.borderRadius.large}px`}
      border={`1px solid ${colors.black[300]}`}
    >
      <Box display="flex" gap="16px">
        <Avatar size="large" iconImage={<IconGraphy icon="EditorLayout.Dashboard" />} />
        <Box>
          <Typography variant="h4" style={{ color: colors.purple[900], fontWeight: '400' }}>
            Application Distribution
          </Typography>

          <Typography variant="body3" style={{ color: colors.neutral[900], fontWeight: '400' }}>
            Here is a detailed breakdown of the application distribution across various stages.
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          background: '#f9f8f9',
          border: `1px solid ${colors.purple[100]}`,
          borderRadius: '8px',
          padding: '16px',
          width: '100%',
          boxSizing: 'border-box',
        }}
        display="flex"
        gap="20px"
      >
        <Box
          sx={{
            flex: '1 1 0%',
            background: 'white',
            border: `1px solid ${colors.black[200]}`,
            borderRadius: `${styles.borderRadius.large}px`,
            padding: '20px',
            boxSizing: 'border-box',
          }}
          display="flex"
          flexDirection="column"
          gap="24px"
        >
          {inProgressItems.map((item) =>
            renderStageRow(item, {
              showBar: true,
              barPercent: getInProgressStageBarPercent(item.value, inProgressBaseline),
            }),
          )}
          {renderStageRow(policyIssuedItem, { showBar: false })}
        </Box>

        <Box
          sx={{
            flex: '1.8 1 0%',
            background: 'white',
            border: `1px solid ${colors.black[200]}`,
            borderRadius: `${styles.borderRadius.large}px`,
            padding: '20px',
            boxSizing: 'border-box',
          }}
          display="flex"
          flexDirection="column"
        >
          <Box display="flex" justifyContent="space-between" width="100%">
            <Box display="flex" gap="4px" flexDirection="column">
              <Typography variant="body1" style={{ fontWeight: '600', color: colors.purple[900] }}>
                Monthly
              </Typography>
              <Typography variant="body3" style={{ color: colors.neutral[900] }}>
                Please choose the year for which you would like to view monthly values.
              </Typography>
            </Box>

            <Dropdown
              value={year}
              size="medium"
              onChange={(e) => {
                const nextYear = toYear(e?.target?.value);
                if (nextYear) {
                  userSelectedYearRef.current = true;
                  setYear(nextYear);
                }
              }}
              options={yearList.map((yearOption) => ({
                text: yearOption.toString(),
                value: yearOption,
              }))}
              multiselect={false}
              placeholder="Select item"
              showSearch={false}
              icon={<IconGraphy icon="TimeDate.CalendarMonth" />}
              iconVisible={true}
              label="Year"
            />
          </Box>

          {monthlyHasValues ? (
            <CustomBarChart
              chartWidth="100%"
              chartHeight={200}
              data={monthlyStageBarData}
              colors={{ value: colors.purpleIrish[100] }}
              barDataKey="value"
              colorKey="color"
              barChartProps={{
                margin: { top: 4, right: 8, left: 0, bottom: 0 },
                barCategoryGap: 36,
              }}
              cartesianGridProps={{ stroke: colors.purple[100] }}
              xAxisProps={{
                axisLine: false,
                interval: 0,
                tick: { fill: '#A39AA9', fontSize: 10, fontWeight: 500 },
              }}
              yAxisProps={{
                domain: [0, chartMax],
                ticks: yTicks,
                tickFormatter: formatVolumeTick,
                tick: { fill: '#6A5E71', fontSize: 10, fontWeight: 500 },
                width: 36,
              }}
              barProps={{
                radius: [0, 0, 0, 0],
                barSize: 21,
              }}
              showTooltip={false}
              styles={{ padding: '0px 8px', boxSizing: 'border-box' }}
            />
          ) : (
            <ChartEmptyBody>
              <DashboardEmptyStateIcon />
              <Typography
                variant="body2"
                style={{
                  textAlign: 'center',
                  color: colors.neutral[900],
                  maxWidth: '360px',
                  fontWeight: '500',
                }}
              >
                No data for this period
              </Typography>
            </ChartEmptyBody>
          )}
          {onApplicationsDrilldown && (
            <Box display="flex" flexWrap="wrap" gap="8px" mt={1}>
              {MONTHLY_FOOTER_CARDS.map(({ shortLabel, filterKey, totalKey }) => (
                <ChartFooterChard
                  key={filterKey}
                  backgroundColor="#F9F8F9"
                  activeColor={colors.purpleIrish[100]}
                  alignment="space-around"
                  title={
                    <Typography
                      variant="small"
                      style={{ fontWeight: '600', color: colors.purple[900] }}
                    >
                      {shortLabel}
                    </Typography>
                  }
                  content={
                    <Typography variant="small" style={{ color: colors.neutral[900] }}>
                      {Number(selectedYearStageTotals[totalKey] || 0).toLocaleString()} Applications
                    </Typography>
                  }
                  onClick={() => onApplicationsDrilldown(filterKey)}
                  style={{ flex: '1 1 100px', minWidth: 90 }}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </AppDistributionContainer>
  );
};

const ChartEmptyBody = styled(Box)`
  width: 100%;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  box-sizing: border-box;
`;

const AppDistributionContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  padding: 16px;
  box-sizing: border-box;

  box-shadow: ${(props) => props.boxShadow};
  border-radius: ${(props) => props.borderRadius};
  border: ${(props) => props.border};

  & > div:nth-of-type(2) {
    @media screen and (max-width: ${({ theme }) => theme.lg}) {
      flex-direction: column;
    }
    @media screen and (max-width: ${({ theme }) => theme.sm}) {
      padding: 0;
    }
    & > div:nth-of-type(2) {
      & > div:nth-of-type(1) {
        @media screen and (max-width: ${({ theme }) => theme.md}) {
          flex-direction: column;
          gap: 20px;
        }
      }

      & > div:nth-of-type(2) {
        @media screen and (max-width: ${({ theme }) => theme.md}) {
          padding: 0;
        }
      }
    }
  }
`;
